import type { AccountInfo, PopupRequest } from '@azure/msal-browser';
import {
  ESignAuthContext,
  ESignProvider,
  ESignResult,
} from '@celito.clients/provider';
import { MsalInstanceManager } from '@celito.clients/singleton';

/**
 * WI-F7 — Entra e-sign provider (ADO #38535).
 *
 * Re-authenticates the CURRENT session user via an MSAL popup for the purpose
 * of an electronic signature, and returns the FRESH id token as the raw JWT.
 *
 * Forces a fresh, deliberate re-auth (not silent SSO): `prompt: 'login'` +
 * `max_age=0` re-challenge the user, and `loginHint` pins the popup to the
 * signed-in identity. MFA runs automatically per the tenant's Entra policy.
 *
 * Reuses the already-configured runtime MSAL instance from
 * {@link MsalInstanceManager} — it does NOT construct its own PCA.
 */

/**
 * Raised when the popup was completed as somebody other than the session user.
 *
 * Its own class so the modal can tell "you signed in as the wrong person" apart
 * from a transport failure — the two have completely different remedies.
 */
export class ESignIdentityMismatchError extends Error {
  public constructor(expected: string) {
    super(
      `This signature must be completed as ${expected}. ` +
        'Sign in again with that account.'
    );
    this.name = 'ESignIdentityMismatchError';
  }
}

const describeAccount = (account: AccountInfo): string =>
  account.username || account.name || 'the signed-in account';

export class EntraPopupProvider implements ESignProvider {
  public readonly provider = 'entra';

  public async authenticate(ctx: ESignAuthContext): Promise<ESignResult> {
    const manager = MsalInstanceManager.getInstance();
    const instance = manager.getMsalInstance();
    const account = manager.getSessionAccount();

    const request: PopupRequest = {
      scopes: manager.getApiScopes(),
      prompt: 'login',
      loginHint: ctx.userHint,
      // Binds this id token to THIS signature request. Without it MSAL mints its
      // own nonce and the token is interchangeable with any other token the same
      // session produced, so a captured one can be replayed for a different
      // signature inside the backend's freshness window.
      nonce: ctx.nonce,
      // max_age=0 forces a fresh authentication instant (no silent reuse).
      extraQueryParameters: { max_age: '0' },
    };

    const result = account
      ? await instance.acquireTokenPopup({ ...request, account })
      : await instance.loginPopup(request);

    if (!result?.idToken) {
      throw new Error('Entra sign-in returned no id token.');
    }

    // `loginHint` only PREFILLS the account picker — `prompt: 'login'` renders a
    // full sign-in screen the user can complete as anybody. A signature carrying
    // somebody else's token is refused HERE rather than sent on for the backend
    // to reject, and refusing it also keeps the session's active account from
    // being re-pointed at that other identity.
    if (
      account &&
      result.account &&
      result.account.homeAccountId !== account.homeAccountId
    ) {
      throw new ESignIdentityMismatchError(describeAccount(account));
    }

    return { rawToken: result.idToken, provider: this.provider };
  }
}
